import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API } from "../App";
import Sidebar from "../components/Sidebar";
import { toast } from "sonner";
import {
  ShareNetwork, TwitterLogo, LinkedinLogo, FacebookLogo, InstagramLogo, PinterestLogo,
  WhatsappLogo, Plus, PaperPlaneTilt, CheckCircle, XCircle, Trash, Warning, ClockCounterClockwise,
} from "@phosphor-icons/react";

const PLATFORMS = [
  { key: "twitter", label: "Twitter / X", icon: TwitterLogo, color: "#1DA1F2", fields: ["access_token", "platform_user_id"], note: "OAuth 2.0 Bearer token with tweet.write + users.read scopes. Create app at developer.twitter.com." },
  { key: "linkedin", label: "LinkedIn", icon: LinkedinLogo, color: "#0A66C2", fields: ["access_token", "platform_user_id"], note: "OAuth 2.0 token with w_member_social scope. Register app at linkedin.com/developers." },
  { key: "facebook", label: "Facebook", icon: FacebookLogo, color: "#1877F2", fields: ["access_token", "platform_user_id"], pageField: true, note: "User access token + one or more page tokens. pages_manage_posts permission required." },
  { key: "instagram", label: "Instagram", icon: InstagramLogo, color: "#E1306C", fields: ["access_token", "platform_user_id"], note: "Instagram Business account ID as platform_user_id. Use same Facebook app with instagram_content_publish scope." },
  { key: "pinterest", label: "Pinterest", icon: PinterestLogo, color: "#E60023", fields: ["access_token", "platform_user_id"], pageField: true, note: "OAuth 2.0 token + boards mapping. Create Business account + app at developers.pinterest.com." },
  { key: "whatsapp", label: "WhatsApp Business", icon: WhatsappLogo, color: "#25D366", fields: ["access_token"], extraField: "phone_number_id", note: "Permanent access token from Meta Business. Requires phone_number_id (WhatsApp Cloud API)." },
];

const SocialHub = ({ user, logout }) => {
  const [tab, setTab] = useState("hub"); // hub | compose | log
  const [conns, setConns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [connectEditor, setConnectEditor] = useState(null); // {platform}
  const [logs, setLogs] = useState([]);

  const role = user?.role || "";
  const allowed = ["admin", "manager", "bdm", "marketing_head"].includes(role);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    try {
      setLoading(true);
      const r = await axios.get(`${API}/social/connections?requester_role=${role}`);
      setConns(r.data);
    } catch { toast.error("Failed to load connections"); }
    finally { setLoading(false); }
  }, [role, allowed]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (tab === "log") {
      axios.get(`${API}/social/publish-log?requester_role=${role}&limit=200`)
        .then(r => setLogs(r.data)).catch(() => setLogs([]));
    }
  }, [tab, role]);

  const disconnect = async (platform) => {
    if (!window.confirm(`Disconnect ${platform}?`)) return;
    try { await axios.delete(`${API}/social/disconnect/${platform}?requester_role=${role}`); toast.success("Disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
  };

  if (!allowed) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Sidebar user={user} logout={logout} />
        <div className="ml-64 p-12">
          <div className="bg-white border border-slate-200 p-8 rounded">
            <Warning size={48} weight="fill" className="text-amber-500 mb-3" />
            <h2 className="text-xl font-bold">Access restricted</h2>
            <p className="text-slate-600 mt-2">Social Hub is available to Admin, Marketing Head, Manager, and BDM roles.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="social-hub-page">
      <Sidebar user={user} logout={logout} />
      <div className="ml-64">
        <div className="bg-white border-b border-slate-200 sticky top-0 z-20">
          <div className="px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShareNetwork size={28} weight="duotone" className="text-[#002FA7]" />
              <div>
                <h1 className="text-xl font-bold text-slate-900">Social Media Hub</h1>
                <p className="text-xs text-slate-500">Unified publisher for Twitter · LinkedIn · Facebook · Instagram · Pinterest · WhatsApp</p>
              </div>
            </div>
          </div>
          <div className="px-6 border-t border-slate-100 flex gap-1">
            {[{ k: "hub", l: "Connections" }, { k: "compose", l: "Compose & Publish" }, { k: "log", l: "Publish Log" }].map(t => (
              <button key={t.k} data-testid={`social-tab-${t.k}`} onClick={() => setTab(t.k)}
                className={`px-4 py-2 text-sm font-semibold border-b-2 ${tab === t.k ? "border-[#002FA7] text-[#002FA7]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
                {t.l}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {loading ? <p>Loading…</p> :
            tab === "hub" ? <ConnectionsGrid conns={conns} onConnect={(p) => setConnectEditor({ platform: p })} onDisconnect={disconnect} /> :
            tab === "compose" ? <Composer conns={conns} role={role} userId={user.user_id} onPublished={load} /> :
            <PublishLog logs={logs} />}
        </div>
      </div>

      {connectEditor && (
        <ConnectModal editor={connectEditor} onClose={() => setConnectEditor(null)}
          role={role} onSaved={() => { setConnectEditor(null); load(); }} />
      )}
    </div>
  );
};

/* ---------- Connection Cards ---------- */
const ConnectionsGrid = ({ conns, onConnect, onDisconnect }) => (
  <div className="grid md:grid-cols-2 gap-4">
    {PLATFORMS.map(p => {
      const c = conns.find(x => x.platform === p.key) || { connected: false };
      const Icon = p.icon;
      return (
        <div key={p.key} data-testid={`platform-card-${p.key}`} className="bg-white border border-slate-200 p-5 rounded flex items-start gap-4">
          <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ background: p.color + "22" }}>
            <Icon size={26} weight="fill" color={p.color} />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <h3 className="font-bold text-slate-900">{p.label}</h3>
              {c.connected ? (
                <span className="text-xs font-semibold bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded flex items-center gap-1">
                  <CheckCircle size={12} weight="fill" /> Connected
                </span>
              ) : (
                <span className="text-xs font-semibold bg-slate-100 text-slate-600 px-2 py-0.5 rounded">Not connected</span>
              )}
            </div>
            <p className="text-xs text-slate-500 mt-1">{p.note}</p>
            {c.connected && (
              <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                {c.last_post_at && <div>Last post: {new Date(c.last_post_at).toLocaleString("en-IN")}</div>}
                {c.last_post_id && <div className="font-mono">ID: {c.last_post_id}</div>}
                {c.last_error && <div className="text-red-600">⚠ {c.last_error.slice(0, 120)}</div>}
              </div>
            )}
            <div className="mt-3 flex gap-2">
              {!c.connected ? (
                <button data-testid={`connect-${p.key}-btn`} onClick={() => onConnect(p.key)}
                  className="px-3 py-1.5 bg-[#002FA7] text-white text-xs rounded flex items-center gap-1">
                  <Plus size={14} weight="bold" /> Connect
                </button>
              ) : (
                <>
                  <button onClick={() => onConnect(p.key)} className="px-3 py-1.5 border border-slate-300 text-slate-700 text-xs rounded">Reconnect</button>
                  <button onClick={() => onDisconnect(p.key)} className="px-3 py-1.5 text-red-600 text-xs border border-red-200 rounded flex items-center gap-1">
                    <Trash size={12} /> Disconnect
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

/* ---------- Connect Modal ---------- */
const ConnectModal = ({ editor, onClose, role, onSaved }) => {
  const p = PLATFORMS.find(x => x.key === editor.platform);
  const [form, setForm] = useState({ access_token: "", platform_user_id: "", page_ids_json: "", phone_number_id: "" });
  const save = async () => {
    if (!form.access_token) { toast.error("Access token required"); return; }
    let page_ids = {};
    if (form.page_ids_json) {
      try { page_ids = JSON.parse(form.page_ids_json); }
      catch { toast.error("Invalid JSON for page IDs"); return; }
    }
    try {
      await axios.post(`${API}/social/connect?requester_role=${role}`, {
        platform: editor.platform,
        access_token: form.access_token,
        platform_user_id: form.platform_user_id,
        platform_page_ids: page_ids,
        extra: form.phone_number_id ? { phone_number_id: form.phone_number_id } : {},
      });
      toast.success(`${p.label} connected`);
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Connect failed"); }
  };
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-40 p-4">
      <div className="bg-white rounded-lg w-full max-w-lg p-5" data-testid={`connect-modal-${p.key}`}>
        <h2 className="text-lg font-bold mb-1">Connect {p.label}</h2>
        <p className="text-xs text-slate-500 mb-4">{p.note}</p>
        <div className="space-y-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Access Token *</label>
            <input data-testid="field-access-token" type="password" value={form.access_token}
              onChange={e => setForm({ ...form, access_token: e.target.value })}
              className="w-full px-3 py-2 border border-slate-300 rounded font-mono text-xs" />
          </div>
          {p.fields.includes("platform_user_id") && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">User / Account ID {p.key === "instagram" && "(Instagram Business Account ID)"}</label>
              <input value={form.platform_user_id} onChange={e => setForm({ ...form, platform_user_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs" />
            </div>
          )}
          {p.pageField && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">
                {p.key === "pinterest" ? "Boards JSON" : "Pages JSON"} <span className="text-slate-400 font-normal">(optional)</span>
              </label>
              <textarea rows="3" value={form.page_ids_json} onChange={e => setForm({ ...form, page_ids_json: e.target.value })}
                placeholder={p.key === "pinterest" ? '{"board_123":"My Board"}' : '{"page_123":"PAGE_ACCESS_TOKEN_HERE"}'}
                className="w-full px-3 py-2 border border-slate-300 rounded font-mono text-xs" />
            </div>
          )}
          {p.extraField === "phone_number_id" && (
            <div>
              <label className="block text-xs font-bold text-slate-600 mb-1">WhatsApp Phone Number ID *</label>
              <input value={form.phone_number_id} onChange={e => setForm({ ...form, phone_number_id: e.target.value })}
                className="w-full px-3 py-2 border border-slate-300 rounded text-xs" />
            </div>
          )}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 border border-slate-300 rounded text-sm">Cancel</button>
          <button data-testid="save-connection-btn" onClick={save} className="px-4 py-2 bg-[#002FA7] text-white rounded text-sm">Connect</button>
        </div>
      </div>
    </div>
  );
};

/* ---------- Composer ---------- */
const Composer = ({ conns, role, userId, onPublished }) => {
  const [selected, setSelected] = useState([]);
  const [content, setContent] = useState("");
  const [mediaUrl, setMediaUrl] = useState("");
  const [pageId, setPageId] = useState("");
  const [recipientPhone, setRecipientPhone] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [publishing, setPublishing] = useState(false);
  const [results, setResults] = useState(null);

  const toggle = (k) => setSelected(s => s.includes(k) ? s.filter(x => x !== k) : [...s, k]);
  const isWA = selected.includes("whatsapp");

  const publish = async () => {
    if (selected.length === 0) { toast.error("Select at least one platform"); return; }
    if (!content) { toast.error("Content required"); return; }
    if (isWA && !recipientPhone && !templateName) {
      toast.error("WhatsApp needs recipient_phone + either content or template_name");
      return;
    }
    setPublishing(true); setResults(null);
    try {
      const r = await axios.post(`${API}/social/publish?requester_role=${role}&requester_id=${userId}`, {
        platforms: selected, content,
        media_urls: mediaUrl ? [mediaUrl] : [],
        page_id: pageId, recipient_phone: recipientPhone, template_name: templateName,
      });
      setResults(r.data);
      const ok = r.data.successful;
      if (ok > 0) toast.success(`Published to ${ok}/${r.data.total} platform(s)`);
      else toast.error("All posts failed — see results below");
      onPublished();
    } catch (e) { toast.error(e.response?.data?.detail || "Publish failed"); }
    finally { setPublishing(false); }
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white border border-slate-200 rounded p-5 mb-4">
        <p className="text-xs font-bold text-slate-600 uppercase tracking-wider mb-3">Target platforms</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {PLATFORMS.map(p => {
            const c = conns.find(x => x.platform === p.key);
            const disabled = !c?.connected;
            const active = selected.includes(p.key);
            const Icon = p.icon;
            return (
              <button key={p.key} data-testid={`select-${p.key}`} type="button"
                disabled={disabled}
                onClick={() => toggle(p.key)}
                className={`px-3 py-2 border rounded text-sm flex items-center gap-2 ${disabled ? "opacity-40 cursor-not-allowed" : ""} ${active ? "border-[#002FA7] bg-blue-50 text-[#002FA7] font-semibold" : "border-slate-300"}`}>
                <Icon size={16} weight="fill" color={p.color} /> {p.label}
                {disabled && <span className="text-xs text-slate-400">(not connected)</span>}
              </button>
            );
          })}
        </div>
        <label className="block text-xs font-bold text-slate-600 mb-1">Content *</label>
        <textarea data-testid="compose-content" rows="5" value={content} onChange={e => setContent(e.target.value)}
          placeholder="Your post content (will be cropped to each platform's limits: Twitter 280, Pinterest 500, etc.)"
          className="w-full px-3 py-2 border border-slate-300 rounded text-sm mb-3" />
        <div className="grid md:grid-cols-2 gap-3 mb-3 text-sm">
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Media URL <span className="text-slate-400 font-normal">(image/video URL — required for IG, Pinterest)</span></label>
            <input value={mediaUrl} onChange={e => setMediaUrl(e.target.value)} placeholder="https://…" className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1">Page / Board / Org ID <span className="text-slate-400 font-normal">(optional)</span></label>
            <input value={pageId} onChange={e => setPageId(e.target.value)} placeholder="e.g. page_123 or org_456" className="w-full px-3 py-2 border border-slate-300 rounded" />
          </div>
          {isWA && (
            <>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">WhatsApp Recipient Phone *</label>
                <input value={recipientPhone} onChange={e => setRecipientPhone(e.target.value)} placeholder="+91XXXXXXXXXX" className="w-full px-3 py-2 border border-slate-300 rounded" />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">Template Name <span className="text-slate-400 font-normal">(optional, for HSM)</span></label>
                <input value={templateName} onChange={e => setTemplateName(e.target.value)} placeholder="order_confirmation" className="w-full px-3 py-2 border border-slate-300 rounded" />
              </div>
            </>
          )}
        </div>
        <div className="flex justify-end">
          <button data-testid="publish-btn" onClick={publish} disabled={publishing}
            className="px-5 py-2.5 bg-[#002FA7] text-white rounded text-sm font-semibold flex items-center gap-2 disabled:opacity-50">
            <PaperPlaneTilt size={14} weight="fill" /> {publishing ? "Publishing…" : `Publish to ${selected.length}`}
          </button>
        </div>
      </div>
      {results && (
        <div className="bg-white border border-slate-200 rounded p-4">
          <p className="text-sm font-bold mb-2">Results — {results.successful}/{results.total} successful</p>
          <div className="space-y-1">
            {results.results.map((r) => (
              <div key={r.platform} className="flex items-center justify-between text-sm py-1 border-b border-slate-100">
                <span className="capitalize font-medium">{r.platform}</span>
                {r.status === "success"
                  ? <span className="text-emerald-600 flex items-center gap-1"><CheckCircle size={14} weight="fill" /> {r.post_id}</span>
                  : <span className="text-red-600 flex items-center gap-1"><XCircle size={14} weight="fill" /> {r.error?.slice(0, 80)}</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/* ---------- Publish Log ---------- */
const PublishLog = ({ logs }) => (
  <div className="bg-white border border-slate-200 rounded overflow-hidden" data-testid="publish-log-table">
    <table className="w-full text-sm">
      <thead className="bg-slate-50 border-b border-slate-200">
        <tr>
          <th className="text-left p-3 text-xs uppercase font-bold text-slate-500">When</th>
          <th className="text-left p-3 text-xs uppercase font-bold text-slate-500">Platform</th>
          <th className="text-left p-3 text-xs uppercase font-bold text-slate-500">Status</th>
          <th className="text-left p-3 text-xs uppercase font-bold text-slate-500">Content</th>
          <th className="text-left p-3 text-xs uppercase font-bold text-slate-500">Result</th>
        </tr>
      </thead>
      <tbody>
        {logs.length === 0 && <tr><td colSpan="5" className="p-6 text-center text-slate-400">No posts yet</td></tr>}
        {logs.map((l) => (
          <tr key={l.log_id || `${l.platform}-${l.created_at}`} className="border-b border-slate-100">
            <td className="p-3 text-xs text-slate-500">{new Date(l.created_at).toLocaleString("en-IN")}</td>
            <td className="p-3 capitalize text-xs">{l.platform}</td>
            <td className="p-3">
              {l.status === "success" ? <CheckCircle size={16} className="text-emerald-600" weight="fill" /> : <XCircle size={16} className="text-red-600" weight="fill" />}
            </td>
            <td className="p-3 text-xs text-slate-700">{(l.content || "").slice(0, 80)}</td>
            <td className="p-3 text-xs text-slate-500">{l.post_id || l.error?.slice(0, 80) || "—"}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default SocialHub;
